/**
 * Mirror of supabase/functions/_shared/llmModelGenerationParams.ts — keep in sync.
 */

const GLOBAL_TOKEN_FALLBACK = { min: 256, max: 8192, default: 4096 }
const MAX_PLAUSIBLE_OUTPUT = 300000

const OPENAI_CHAT_TEMP = { supported: true, min: 0, max: 2, default: 1, step: 0.1 }

function openAiTemperatureUnsupported(modelId) {
  const m = modelId.toLowerCase()
  if (/^o[0-9]/.test(m)) return true
  if (m.startsWith('gpt-5')) return true
  if (m.includes('reasoning')) return true
  return false
}

const OPENAI_OUTPUT_CATALOG = [
  { prefix: 'gpt-4o-mini', max: 16384, default: 4096 },
  { prefix: 'gpt-4o', max: 16384, default: 4096 },
  { prefix: 'gpt-4.1', max: 32768, default: 4096 },
  { prefix: 'gpt-4', max: 8192, default: 4096 },
  { prefix: 'chatgpt-4o', max: 16384, default: 4096 },
  { prefix: 'o1', max: 100000, default: 8192 },
  { prefix: 'o3', max: 100000, default: 8192 },
  { prefix: 'o4', max: 100000, default: 8192 },
  { prefix: 'gpt-5', max: 128000, default: 8192 },
]

const ANTHROPIC_TEMP = { supported: true, min: 0, max: 1, default: 1, step: 0.05 }

/** Longest prefix wins — https://platform.claude.com/docs/en/about-claude/models/overview */
const ANTHROPIC_OUTPUT_CATALOG = [
  { prefix: 'claude-opus-4-7', max: 128000, default: 128000 },
  { prefix: 'claude-opus-4-6', max: 128000, default: 128000 },
  { prefix: 'claude-opus-4-5', max: 64000, default: 64000 },
  { prefix: 'claude-opus-4-1', max: 32000, default: 32000 },
  { prefix: 'claude-opus-4-0', max: 32000, default: 32000 },
  { prefix: 'claude-opus-4', max: 32000, default: 32000 },
  { prefix: 'claude-sonnet-4-6', max: 64000, default: 64000 },
  { prefix: 'claude-sonnet-4-5', max: 64000, default: 64000 },
  { prefix: 'claude-sonnet-4-0', max: 64000, default: 64000 },
  { prefix: 'claude-sonnet-4', max: 64000, default: 64000 },
  { prefix: 'claude-haiku-4-5', max: 64000, default: 64000 },
  { prefix: 'claude-haiku-4', max: 64000, default: 64000 },
  { prefix: 'claude-3-5-sonnet', max: 8192, default: 8192 },
  { prefix: 'claude-3-5-haiku', max: 8192, default: 8192 },
  { prefix: 'claude-3-opus', max: 4096, default: 4096 },
]

const GEMINI_TEMP = { supported: true, min: 0, max: 2, default: 1, step: 0.1 }

function matchCatalogPrefix(modelId, catalog) {
  const id = modelId.toLowerCase()
  let best = null
  for (const row of catalog) {
    if (id === row.prefix || id.startsWith(row.prefix + '-') || id.startsWith(row.prefix)) {
      if (!best || row.prefix.length > best.prefix.length) best = row
    }
  }
  return best
}

function buildMaxOutput(apiMax, apiDefault, catalogRow) {
  const catalogMax = catalogRow?.max ?? GLOBAL_TOKEN_FALLBACK.max
  const catalogDef = catalogRow?.default ?? GLOBAL_TOKEN_FALLBACK.default
  let max = catalogMax
  if (apiMax != null && apiMax > 0 && apiMax <= MAX_PLAUSIBLE_OUTPUT) {
    max = Math.min(catalogMax, apiMax)
  }
  const min = Math.min(GLOBAL_TOKEN_FALLBACK.min, max)
  let def = catalogDef
  if (apiDefault != null && apiDefault > 0 && apiDefault <= MAX_PLAUSIBLE_OUTPUT) {
    def = Math.min(apiDefault, max)
  }
  return {
    min,
    max: Math.max(min, max),
    default: Math.min(Math.max(def, min), max),
  }
}

function resolveTemperature(provider, modelId, apiHints = {}) {
  if (apiHints.temperatureSupported === false) {
    return { supported: false, min: 1, max: 1, default: 1, step: 0.1 }
  }
  const p = String(provider || 'anthropic').toLowerCase()
  const id = String(modelId || '').trim()
  if (p === 'openai' && id && openAiTemperatureUnsupported(id)) {
    return { supported: false, min: 1, max: 1, default: 1, step: 0.1 }
  }
  if (p === 'gemini') {
    return {
      supported: true,
      min: apiHints.temperatureMin ?? GEMINI_TEMP.min,
      max: apiHints.temperatureMax ?? GEMINI_TEMP.max,
      default: GEMINI_TEMP.default,
      step: GEMINI_TEMP.step,
    }
  }
  if (p === 'openai') return OPENAI_CHAT_TEMP
  return ANTHROPIC_TEMP
}

export function resolveGenerationParams(provider, modelId, apiHints = {}) {
  const id = String(modelId || '').trim()
  const p = String(provider || 'anthropic').toLowerCase()
  const temperature = resolveTemperature(p, id, apiHints)

  if (p === 'openai') {
    const cat = id ? matchCatalogPrefix(id, OPENAI_OUTPUT_CATALOG) : null
    return {
      maxOutputTokens: buildMaxOutput(apiHints.maxOutputTokens, apiHints.defaultOutputTokens, cat),
      temperature,
    }
  }

  if (p === 'gemini') {
    const maxOut = buildMaxOutput(apiHints.maxOutputTokens, apiHints.defaultOutputTokens, null)
    if (apiHints.maxOutputTokens != null && apiHints.maxOutputTokens > 0) {
      maxOut.max = Math.min(maxOut.max, apiHints.maxOutputTokens)
      maxOut.default = Math.min(maxOut.default, maxOut.max)
    } else {
      maxOut.max = 8192
      maxOut.default = Math.min(maxOut.default, maxOut.max)
    }
    return { maxOutputTokens: maxOut, temperature }
  }

  const cat = id ? matchCatalogPrefix(id, ANTHROPIC_OUTPUT_CATALOG) : null
  return {
    maxOutputTokens: buildMaxOutput(apiHints.maxOutputTokens, apiHints.defaultOutputTokens, cat),
    temperature,
  }
}

export function clampMaxOutputTokens(value, limits) {
  if (value == null || !Number.isFinite(value)) return limits.maxOutputTokens.default
  const t = Math.floor(value)
  if (t < limits.maxOutputTokens.min) return limits.maxOutputTokens.min
  if (t > limits.maxOutputTokens.max) return limits.maxOutputTokens.max
  return t
}

export function resolveTemperatureForApi(value, limits) {
  if (!limits.temperature.supported) return null
  if (value == null || !Number.isFinite(value)) return limits.temperature.default
  const v = Number(value)
  if (v < limits.temperature.min) return limits.temperature.min
  if (v > limits.temperature.max) return limits.temperature.max
  return Math.round(v / limits.temperature.step) * limits.temperature.step
}

export function sliderPosToTokens(pos, min, max) {
  const lo = Math.log(Math.max(min, 1))
  const hi = Math.log(Math.max(max, min + 1))
  const v = Math.exp(lo + pos * (hi - lo))
  return snapTokenValue(Math.round(v), min, max)
}

export function tokensToSliderPos(value, min, max) {
  const v = Math.max(min, Math.min(max, value))
  const lo = Math.log(Math.max(min, 1))
  const hi = Math.log(Math.max(max, min + 1))
  if (hi <= lo) return 0
  return (Math.log(Math.max(v, 1)) - lo) / (hi - lo)
}

export function snapTokenValue(n, min, max) {
  let step = 256
  if (max > 32000) step = 1024
  else if (max > 8192) step = 512
  const snapped = Math.round(n / step) * step
  return Math.max(min, Math.min(max, snapped))
}

export function snapTemperature(value, limits) {
  if (value == null) return null
  const step = limits.temperature.step
  const v = Math.max(limits.temperature.min, Math.min(limits.temperature.max, value))
  const snapped = Math.round(v / step) * step
  return Math.round(snapped * 100) / 100
}

function normTempValue(t) {
  if (t == null || !Number.isFinite(Number(t))) return null
  return Math.round(Number(t) * 100) / 100
}

function normMaxValue(maxOut) {
  return maxOut == null ? null : Math.floor(maxOut)
}

function maxTokensEquivalent(stored, normalized, limits) {
  const vm = normMaxValue(stored)
  const nm = normMaxValue(normalized)
  if (vm === nm) return true
  const modelDefault = limits?.maxOutputTokens?.default ?? null
  if (vm == null && nm === modelDefault) return true
  if (nm == null && vm === modelDefault) return true
  return false
}

function temperatureEquivalent(stored, normalized, limits) {
  const vt = normTempValue(stored)
  const nt = normTempValue(normalized)
  if (vt === nt) return true
  if (!limits?.temperature?.supported) return vt == null && nt == null
  const modelDefault = limits.temperature.default
  if (vt == null && nt === modelDefault) return true
  if (nt == null && vt === modelDefault) return true
  return false
}

/** Values stored on ai_agent_prompt_versions for dedupe (null = use model default). */
export function normalizeForDedup({
  systemPrompt,
  maxOut,
  temperature,
  useDefaultMaxTokens,
  useDefaultTemperature,
  limits,
}) {
  const prompt = (systemPrompt || '').trim()
  let normMax = null
  if (!useDefaultMaxTokens && maxOut != null && Number.isFinite(Number(maxOut))) {
    normMax = Math.floor(Number(maxOut))
  }
  let normTemp = null
  if (!useDefaultTemperature && limits?.temperature?.supported && temperature != null) {
    normTemp = normTempValue(temperature)
  }
  return { prompt, maxOut: normMax, temperature: normTemp }
}

export function findDuplicatePromptVersion(versions, systemPrompt, maxOut, temperature, limits = null) {
  const prompt = (systemPrompt || '').trim()
  const normMax = normMaxValue(maxOut)
  const normTemp = normTempValue(temperature)
  for (const v of versions) {
    const vp = (v.system_prompt || '').trim()
    if (vp !== prompt) continue
    if (limits) {
      if (!maxTokensEquivalent(v.max_output_tokens, normMax, limits)) continue
      if (!temperatureEquivalent(v.temperature, normTemp, limits)) continue
    } else {
      const vm =
        v.max_output_tokens != null && Number.isFinite(Number(v.max_output_tokens))
          ? Math.floor(Number(v.max_output_tokens))
          : null
      const vt = normTempValue(v.temperature)
      if (vm !== normMax || vt !== normTemp) continue
    }
    return v
  }
  return null
}

/** Build limits from integration-probe model row or resolveGenerationParams */
export function limitsFromModelRow(provider, modelId, row) {
  if (!row) return resolveGenerationParams(provider, modelId, {})
  return resolveGenerationParams(provider, modelId, {
    maxOutputTokens: row.outputTokenLimit ?? null,
    defaultOutputTokens: row.defaultOutputTokens ?? null,
    temperatureMin: row.temperatureMin,
    temperatureMax: row.temperatureMax,
    temperatureSupported: row.temperatureSupported,
  })
}
